/**
 * Client ของใบสั่งผลิต — พูดกับ `/v1/production-orders` ตาม payload contract
 * ที่ freeze ไว้ใน `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ `lib/products/client.ts` และ `lib/bom/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ยังไม่มีใบสั่งผลิต" — รายการว่าง
 *   กับโหลดไม่สำเร็จต้องแยกกันให้ขาด
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *   (D-049 อนุญาตเฉพาะ session token) ใบสั่งผลิตที่ cache ไว้คือภาพเก่าของสถานะที่
 *   อาจถูกสั่งปล่อยหรือยกเลิกไปแล้ว และหน้างานจะทำตามภาพเก่านั้น
 *
 * **สิ่งที่ไฟล์นี้ไม่มีโดยเจตนา: `complete`, `close` และ `cancel`** — ทั้งสาม
 * endpoint เป็นงานของ `WO-P1-M5-002` และยังไม่มีอยู่จริง การเขียนฟังก์ชันไว้ก่อน
 * จะได้หน้าจอที่ยิงไปชน `404` แล้วรายงานเป็น "เซิร์ฟเวอร์ผิดพลาด" ซึ่งผู้ใช้ทำอะไร
 * ต่อไม่ได้เลย ⚠️ **เมื่อ `WO-P1-M5-002` ลง ต้องกลับมาเติมที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateProductionOrderBody,
  ProductionOrder,
  ProductionOrderListFilters,
  ProductionOrderListPage,
  ProductionOrderOperation,
  ProductionOrderTransitionBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ เหมือน `PRODUCTS_PATH`
 */
export const PRODUCTION_ORDERS_PATH = '/v1/production-orders';

export function productionOrderPath(orderId: string): string {
  return `${PRODUCTION_ORDERS_PATH}/${encodeURIComponent(orderId)}`;
}

/** `POST .../{id}/release` — เส้นทางเดียวที่กลุ่มนี้เปลี่ยนสถานะได้ใน M5 */
export function productionOrderReleasePath(orderId: string): string {
  return `${productionOrderPath(orderId)}/release`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ หน้าจอต้องแสดงค่านี้ ไม่ใช่กลืนเป็น "ผิดพลาด" ทั่วไป
   * `NO_ACTIVE_BOM` กับ `NO_ACTIVE_ROUTING` ต่างกันแค่ค่านี้ค่าเดียว และวิศวกรต้อง
   * รู้ว่าต้องไปสร้างอะไรต่อ
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * ไม่ยุบ 403/404/409/422 เข้าด้วยกันโดยเจตนา: `403` ของกลุ่มนี้แปลว่า **Plant นี้
 * อยู่นอกขอบเขตของคุณ** (D-004 — contract ระบุว่านอก scope ตอบ `403` ไม่ใช่ `404`)
 * ส่วน `409` แปลว่ามีคนขยับ order ใบนี้ไปแล้วระหว่างที่หน้าจอเปิดค้าง คนละเรื่อง
 * กันคนละครึ่ง และผู้ใช้ต้องทำต่างกันในแต่ละกรณี
 */
export type ProductionOrderFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ProductionOrderListResult =
  | { readonly status: 'ready'; readonly page: ProductionOrderListPage }
  | ProductionOrderFailure;

export type ProductionOrderResult =
  | { readonly status: 'ready'; readonly order: ProductionOrder }
  | ProductionOrderFailure;

export interface ProductionOrderRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* --------------------------------------------------------------- query ---- */

/**
 * ต่อ query string ตามลำดับที่ contract เขียนไว้
 *
 * `plant_code` มาก่อนเสมอเพราะมันเป็นขอบเขตสิทธิ์ ไม่ใช่ตัวกรองความสะดวก (D-004)
 * ค่าที่เป็นสตริงว่างถูกตัดทิ้ง — `?status=` ไม่ได้แปลว่า "สถานะว่างเปล่า" แต่แปลว่า
 * ผู้ใช้ยังไม่ได้เลือก
 */
export function buildProductionOrdersQuery(filters: ProductionOrderListFilters = {}): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | undefined) => {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(key, value.trim());
    }
  };

  append('plant_code', filters.plant_code);
  append('status', filters.status);
  append('product_code', filters.product_code);
  if (typeof filters.limit === 'number' && Number.isInteger(filters.limit)) {
    params.append('limit', String(filters.limit));
  }
  append('cursor', filters.cursor);

  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ ฟิลด์ที่หายไป
 * ถือเป็น null ได้ แต่ตัวเลขที่มาในช่องที่ต้องเป็นข้อความถือว่าผิดรูป
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

export function parseProductionOrderOperation(raw: unknown): ProductionOrderOperation | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const seq = positiveInteger(record.operation_seq);
  if (seq === null) {
    return null;
  }

  const workCenter = nullableString(record.work_center_code);
  const tool = nullableString(record.tool_code);
  const std = nullableString(record.std_time);
  const setup = nullableString(record.setup_time);
  if (
    workCenter === undefined ||
    tool === undefined ||
    std === undefined ||
    setup === undefined
  ) {
    return null;
  }

  return {
    operation_seq: seq,
    work_center_code: workCenter,
    tool_code: tool,
    std_time: std,
    setup_time: setup,
  };
}

/**
 * รหัสของ order หนึ่งใบ
 *
 * ⚠️ `docs/ERD.md` ตั้งชื่อคอลัมน์ว่า `production_order_id` แต่ contract ไม่ได้
 * freeze ชื่อคีย์ในคำตอบ `GET` ไว้ (ตารางเขียนแค่ `/production-orders/{id}`)
 * ที่นี่จึงอ่าน `production_order_id` ก่อนแล้วรับ `order_id` เป็นทางถอย แทนการ
 * เลือกข้างให้ backend ซึ่งกำลังถูกสร้างขนานกันจากเอกสารฉบับเดียวกัน —
 * การเลือกข้างเงียบ ๆ แล้วผิดจะกลายเป็นหน้าจอที่ขึ้น "ผิดรูป" ทั้งที่เซิร์ฟเวอร์
 * ตอบถูกตามที่ตัวเองอ่านเอกสาร **รายงานเป็น blocker แล้ว**
 */
function orderId(record: Record<string, unknown>): string | null {
  return requiredString(record.production_order_id) ?? requiredString(record.order_id);
}

export function parseProductionOrder(raw: unknown): ProductionOrder | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const id = orderId(record);
  const status = requiredString(record.status);
  if (id === null || status === null) {
    return null;
  }

  const orderNo = nullableString(record.order_no);
  const plantCode = nullableString(record.plant_code);
  const productCode = nullableString(record.product_code);
  // ⚠️ `planned_quantity` ผ่าน `nullableString` ไม่ใช่ตัวแปลงตัวเลข: `NUMERIC(18,4)`
  // ที่ผ่าน `Number` มาแล้วคือค่าที่เพี้ยนไปก่อนถึงที่นี่ การรับไว้คือการกลืนมัน
  const plannedQuantity = nullableString(record.planned_quantity);
  const plannedStart = nullableString(record.planned_start_date);
  const plannedEnd = nullableString(record.planned_end_date);
  const sourceType = nullableString(record.source_type);
  const sourceReference = nullableString(record.source_reference_id);
  const bomId = nullableString(record.bom_id);
  const routingId = nullableString(record.routing_id);
  const createdBy = nullableString(record.created_by_employee_id);
  const releasedBy = nullableString(record.released_by_employee_id);
  const createdAt = nullableString(record.created_at);
  const updatedAt = nullableString(record.updated_at);
  if (
    orderNo === undefined ||
    plantCode === undefined ||
    productCode === undefined ||
    plannedQuantity === undefined ||
    plannedStart === undefined ||
    plannedEnd === undefined ||
    sourceType === undefined ||
    sourceReference === undefined ||
    bomId === undefined ||
    routingId === undefined ||
    createdBy === undefined ||
    releasedBy === undefined ||
    createdAt === undefined ||
    updatedAt === undefined
  ) {
    return null;
  }

  // operation ที่หายไปทั้งก้อนถือเป็นชุดว่าง (list ไม่จำเป็นต้องส่ง snapshot มาด้วย
  // และ order ที่ยังไม่ release ก็ยังไม่มี) แต่ค่าที่มาแล้วไม่ใช่ array หรือมีแถว
  // ผิดรูป = payload ผิดรูป — snapshot ที่แสดงไม่ครบคือขั้นตอนที่หน้างานทำตกไป
  const rawOperations = record.operations;
  const operations: ProductionOrderOperation[] = [];
  if (rawOperations !== undefined && rawOperations !== null) {
    if (!Array.isArray(rawOperations)) {
      return null;
    }
    for (const entry of rawOperations) {
      const parsed = parseProductionOrderOperation(entry);
      if (parsed === null) {
        return null;
      }
      operations.push(parsed);
    }
  }

  return {
    production_order_id: id,
    order_no: orderNo,
    plant_code: plantCode,
    product_code: productCode,
    planned_quantity: plannedQuantity,
    planned_start_date: plannedStart,
    planned_end_date: plannedEnd,
    status,
    source_type: sourceType,
    source_reference_id: sourceReference,
    bom_id: bomId,
    routing_id: routingId,
    created_by_employee_id: createdBy,
    released_by_employee_id: releasedBy,
    created_at: createdAt,
    updated_at: updatedAt,
    operations,
  };
}

/**
 * อ่านรายการได้ทั้งสอง envelope ที่ contract เปิดทางไว้
 *
 * ⚠️ contract freeze **body ของ `POST`** ไว้ครบ แต่หัวข้อ `GET` บอกแค่ว่า
 * "List ตาม plant scope" โดยไม่ได้ freeze envelope — จะเป็น `{ items, next_cursor }`
 * แบบ `GET /v1/products` หรือ array เปล่า ๆ ก็อ่านได้ทั้งคู่ ด้วยเหตุผลเดียวกับ
 * `parseProductChangeRequestList()` **รายงานเป็น blocker แล้ว**
 */
export function parseProductionOrderListPage(raw: unknown): ProductionOrderListPage | null {
  const rawItems = Array.isArray(raw)
    ? raw
    : typeof raw === 'object' && raw !== null
      ? (raw as Record<string, unknown>).items
      : undefined;
  if (!Array.isArray(rawItems)) {
    return null;
  }

  const items: ProductionOrder[] = [];
  for (const entry of rawItems) {
    const parsed = parseProductionOrder(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  if (Array.isArray(raw)) {
    return { items, next_cursor: null };
  }

  const cursor = nullableString((raw as Record<string, unknown>).next_cursor);
  if (cursor === undefined) {
    return null;
  }
  // cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
  // จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | ProductionOrderFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: ProductionOrderRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/production-orders` — list ที่ถูกจำกัดด้วย plant scope ของผู้เรียกเสมอ */
export async function fetchProductionOrders(
  filters: ProductionOrderListFilters = {},
  options: ProductionOrderRequestOptions = {},
): Promise<ProductionOrderListResult> {
  const result = await requestPayload(
    `${PRODUCTION_ORDERS_PATH}${buildProductionOrdersQuery(filters)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseProductionOrderListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/production-orders/{id}` — รายละเอียด + snapshot ของ routing */
export async function fetchProductionOrder(
  orderIdValue: string,
  options: ProductionOrderRequestOptions = {},
): Promise<ProductionOrderResult> {
  const result = await requestPayload(
    productionOrderPath(orderIdValue),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const order = parseProductionOrder(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/**
 * แปลง body ให้เหลือเฉพาะห้าคีย์ที่ contract รับ — และไม่มีคีย์ที่หก
 *
 * ด่านที่สองต่อจากชนิดข้อมูล: ต่อให้ object ที่ส่งเข้ามามีคีย์แปลกปลอมติดมา (เช่น
 * state ของฟอร์มที่ถูก spread ทั้งก้อน) สิ่งที่ออกไปกับ request ยังเป็นเฉพาะคีย์ที่นี่
 * **`status`, `order_no` และ `source_type` จึงไปถึงเซิร์ฟเวอร์ไม่ได้เลย** ซึ่งเป็น
 * เงื่อนไขที่ทำให้ state machine ข้อ 14 และ D-034 ยังมีความหมาย — เขียนคีย์ทีละตัว
 * ด้วยเหตุผลเดียวกับ `serializeCreateProductBody()`
 */
export function serializeCreateProductionOrderBody(
  body: CreateProductionOrderBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    product_code: body.product_code,
    planned_quantity: body.planned_quantity,
    planned_start_date: body.planned_start_date,
    planned_end_date: body.planned_end_date,
  };
}

/**
 * `POST /v1/production-orders` — สร้างที่ `DRAFT` เสมอ
 *
 * ไม่ส่ง `Idempotency-Key`: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/accounting การใส่กับกลุ่มนี้จะทำให้
 * ความหมายของ `request_dedup` เบลอ — `DRAFT` ยังไม่แตะสต๊อกหรือบัญชีเลย
 */
export async function createProductionOrder(
  body: CreateProductionOrderBody,
  options: ProductionOrderRequestOptions = {},
): Promise<ProductionOrderResult> {
  const result = await requestPayload(
    PRODUCTION_ORDERS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateProductionOrderBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const order = parseProductionOrder(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/** body ของ `release` — คีย์เดียว ด้วยเหตุผลเดียวกับ transition ของ product */
export function serializeReleaseBody(expectedStatus: string): ProductionOrderTransitionBody {
  return { expected_status: expectedStatus };
}

/**
 * `POST /v1/production-orders/{id}/release`
 *
 * `expectedStatus` เป็น parameter บังคับโดยเจตนา ไม่ใช่ค่าที่ฟังก์ชันนี้ไปอ่านเอง
 * จาก record: ผู้เรียกต้องส่ง **สถานะที่ผู้ใช้เห็นอยู่บนจอ** มาเอง ถ้าปล่อยให้
 * client ไปหยิบสถานะล่าสุดมาใส่ให้เอง guard ก็จะยืนยันได้แค่ว่า "ข้อมูลที่เพิ่ง
 * โหลดมาตรงกับข้อมูลที่เพิ่งโหลดมา" ซึ่งไม่กันอะไรเลย
 *
 * สิ่งที่คำสั่งนี้ทำฝั่งเซิร์ฟเวอร์ และเป็นเหตุผลที่มันเป็นคำสั่งที่กลับไม่ได้:
 * snapshot `bom_id`/`routing_id` ที่ใช้อยู่ ณ วินาทีนั้นลง order แล้ว copy operation
 * ทุกแถวเข้า `production_order_operation` — revision ใหม่ที่วิศวกรออกพรุ่งนี้จะไม่
 * กระทบ order ใบนี้อีกเลย (`BUSINESS_RULES.md` ข้อ 2)
 */
export async function releaseProductionOrder(
  orderIdValue: string,
  expectedStatus: string,
  options: ProductionOrderRequestOptions = {},
): Promise<ProductionOrderResult> {
  const result = await requestPayload(
    productionOrderReleasePath(orderIdValue),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeReleaseBody(expectedStatus)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const order = parseProductionOrder(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/**
 * อ่าน `details.current_status` จากความล้มเหลวที่มี envelope
 *
 * `409` ของ transition ตอบสถานะจริงมาด้วยเสมอตาม contract และนั่นคือสิ่งเดียวที่
 * บอกผู้ใช้ได้ว่าหน้าจอที่เปิดค้างไว้ตกยุคไปเป็นอะไรแล้ว — คืน `null` เมื่อไม่มีค่า
 * ที่อ่านได้แทนที่จะเดา เพราะการเดาสถานะของ order ที่คนอื่นเพิ่งขยับ คือความผิดพลาด
 * ชนิดเดียวกับที่ guard นี้มีไว้ป้องกัน
 */
export function currentStatusFromFailure(failure: ProductionOrderFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
